// Dynamic flag service that retrieves flags from Gonfalon's internal config API
// Replaces hardcoded flags with real-time flag values

type GonfalonConfig = {
  allClientSideFlags: { [key: string]: unknown };
  baseUri: string;
  clientSideId: string;
  frontendVersion: string;
  backendVersion: string;
  userId?: string;
  userEmail?: string;
  accountId?: string;
  planInfo?: string;
  subscriptionState?: string;
  // Add other config properties as needed
};

class FlagService {
  private cachedFlags: { [key: string]: unknown } = {};

  private cachedConfig: Partial<GonfalonConfig> = {};

  private isInitialized = false;

  private initPromise: Promise<void> | null = null;

  /**
   * Fetch the current configuration from Gonfalon's internal API
   */
  private async fetchConfig(): Promise<GonfalonConfig> {
    try {
      // Import the config to get the proper Gonfalon URL for this environment
      const { default: config } = await import('../config');

      let configUrl: string;

      // Build headers exactly like the existing gonfalon/api.ts does
      const headers: HeadersInit = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        // Add X-LD-Private header when localPrivateAuth is true (same as existing API)
        ...(config.gonfalon.localPrivateAuth && { 'X-LD-Private': 'allowed' }),
      };

      if (config.gonfalon.localPrivateAuth) {
        // Local development: use relative URL so RSPack proxy handles the request
        configUrl = '/internal/config/anonymous';
      } else {
        // Production: use full URL with CORS (same as existing billing API calls)
        const gonfalonHost = config.gonfalon?.host;
        if (!gonfalonHost) {
          throw new Error('No Gonfalon host configured');
        }
        configUrl = `${gonfalonHost}/internal/config/anonymous`;
      }

      const response = await fetch(configUrl, {
        credentials: 'include', // Include cookies for authentication
        headers,
      });

      if (!response.ok) {
        throw new Error(`Config API returned ${response.status}: ${response.statusText}`);
      }

      const gonfalonConfig: GonfalonConfig = await response.json();
      console.log('Fetched dynamic config for Gonfalon packages:', gonfalonConfig);
      return gonfalonConfig;
    } catch (error) {
      console.error('Failed to fetch config from Gonfalon API:', error);
      console.log('Falling back to static flags');

      // Return fallback config with static flags if anonymous endpoint fails
      return {
        allClientSideFlags: this.getStaticFallbackFlags(),
        baseUri: (window.ldConstants?.baseUri as string) || window.location.origin,
        clientSideId: (window.ldConstants?.clientSideId as string) || '',
        frontendVersion: '1.0.0',
        backendVersion: '1.0.0',
      };
    }
  }

  /**
   * Get static fallback flags (current hardcoded flags from globals.ts)
   */
  private getStaticFallbackFlags(): { [key: string]: unknown } {
    return {
      'enable-out-of-the-box-roles-fe': false,
      'enable-no-access-role': false,
      'enable-teams-visibility': 'teams_off',
      'enable-audit-log-actor-display': false,
      'enable-general-tab': false,
      'enable-power-users-tab': false,
      'show-provisioning-mode-toggle': false,
      'enable-ootb-roles': false,
      'throw-errors-in-router-hooks': false,
      'enable-self-serve-usage-based-billing': false,
      'enable-plan-usage-charts': true,
      'plan-usage-tabs-override': {
        experimentationEventsTab: ['planEnabled'],
        observabilityErrorsTab: ['planEnabled'],
        observabilityTracesTab: ['planEnabled'],
        clientSideMAUTab: ['planEnabled'],
        observabilitySessionsTab: ['planEnabled'],
        dataExportTab: ['planEnabled'],
        observabilityLogsTab: ['planEnabled'],
        aiExecutionsTab: ['planDisabled', 'diagnosticDisabled'],
      },
      'use-http-method-override': false,
      'frontend-query-cache-persistence': 'localstorage',
      'enable-self-serve-developer-2025-plan-selection': false,
      'enable-self-serve-foundation-2025-plan-selection': false,
      'enable-service-connections-usage-sdk-app-id-breakdown': true,
    };
  }

  /**
   * Initialize the flag service by fetching config from Gonfalon
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initPromise) {
      await this.initPromise;
      return;
    }

    this.initPromise = (async () => {
      try {
        console.log('Initializing dynamic flag service...');
        const config = await this.fetchConfig();

        // Cache the config
        this.cachedConfig = config;
        this.cachedFlags = config.allClientSideFlags;

        // Update window.ldConstants for compatibility with existing code
        this.updateWindowConstants(config);

        this.isInitialized = true;
        console.log('Dynamic flag service initialized successfully');
      } catch (error) {
        console.error('Failed to initialize flag service:', error);
        // Still mark as initialized to prevent retry loops
        this.isInitialized = true;
      }
    })();

    await this.initPromise;
  }

  /**
   * Update window.ldConstants with the fetched config
   */
  private updateWindowConstants(config: GonfalonConfig): void {
    if (!window.ldConstants) {
      (window as unknown as { ldConstants: { [key: string]: unknown } }).ldConstants = {};
    }

    // Update with new values from Gonfalon
    window.ldConstants = {
      ...window.ldConstants,
      allClientSideFlags: config.allClientSideFlags,
      baseUri: config.baseUri,
      clientSideId: config.clientSideId,
      frontendVersion: config.frontendVersion,
      backendVersion: config.backendVersion,
      userId: config.userId || null,
      userEmail: config.userEmail || null,
      accountId: config.accountId || null,
      planInfo: config.planInfo || null,
      subscriptionState: config.subscriptionState || null,
    };

    console.log('Updated window.ldConstants with dynamic config');
  }

  /**
   * Get all client-side flags
   */
  getAllClientSideFlags(): { [key: string]: unknown } {
    if (!this.isInitialized) {
      console.warn('Flag service not initialized, returning cached flags');
    }
    return this.cachedFlags;
  }

  /**
   * Get a specific flag value
   */
  getFlag(flagKey: string): unknown {
    return this.cachedFlags[flagKey];
  }

  /**
   * Refresh flags by fetching latest config from Gonfalon
   */
  async refreshFlags(): Promise<void> {
    try {
      console.log('Refreshing flags from Gonfalon...');
      const config = await this.fetchConfig();

      this.cachedConfig = config;
      this.cachedFlags = config.allClientSideFlags;
      this.updateWindowConstants(config);

      console.log('Flags refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh flags:', error);
    }
  }

  /**
   * Get cached config (for other constants like baseUri, etc.)
   */
  getConfig(): Partial<GonfalonConfig> {
    return this.cachedConfig;
  }
}

// Create singleton instance
export const flagService = new FlagService();

// Helper function for backwards compatibility
export const getAllClientSideFlags = (): { [key: string]: unknown } => flagService.getAllClientSideFlags();
